import * as React from "react";
import {Store} from "./store";
import {observer} from "mobx-react";
import {
  Wrapper,
  Content,
  Table,
  TableHead,
  TableHeadRow,
  TableHeadCell,
  TableBody,
  TableCell,
  TableCellAlert,
  TableRow,
  Loader,
} from "./components";
import { ProtableTopBar } from "./top";
import { ProtableControls } from "./controls";
import { ProtableFilters } from "./filters";
import { ProtableExport } from "./export";
import { RotateLoader } from "../../loaders/rotate/index";
import { Query, FetchFunction } from "./types";
import { FaExclamation } from "react-icons/fa";
import {Checkbox} from "../../form/checkbox";

interface Column<T extends { _id: string; }> {
  title: React.ReactNode;
  breakpoint?: number;
  className?: string;
  width?: number;
  render: (row: T) => React.ReactNode;
  hide?: boolean;
}

interface Props<T extends { _id: string; }> {
  items: T[];
  count: number;
  screenWidth: number;
  query: Query;
  columns: Array<Column<T>>;
  rowAlert?: (row: T) => boolean;
  fetch: FetchFunction;
  onClick: (row: T) => void;
  exportUrl: string;
  onPageChange?: (page: number) => void;
  region: RestaurantSchema["settings"]["region"];
  selectedRows?: string[];
  onChangeRowSelect?: (selected: string[]) => void;
}

@observer
export class Protable<T extends { _id: string; }> extends React.Component<Props<T>> {

  store: Store<T>;

  constructor(props: Props<T>) {
    super(props);
    this.state = {};
    this.store = new Store(props.fetch);
    this.store.update({
      items: props.items,
      count: props.count,
    });
  }

  componentDidMount() {
    this.store.fetch();
  }

  componentDidUpdate() {
    this.store.update({
      items: this.props.items,
      count: this.props.count,
    });
  }

  render() {
    const { screenWidth, columns, query, onClick, exportUrl, rowAlert, onPageChange, region, onChangeRowSelect, selectedRows } = this.props;
    const { items, loading, error } = this.store.s;
    return (
      <Wrapper>

        <Content>

          <ProtableTopBar<T> store={this.store}/>
          <ProtableFilters<T> store={this.store} query={query}/>
          <ProtableExport<T> store={this.store} exportUrl={exportUrl} region={region}/>

          <Table>
            <TableHead>
              <TableHeadRow>
                {!!onChangeRowSelect && (
                  <TableHeadCell
                    className={""}
                  />
                )}
                {columns.map((c, i) => {
                  if (c.hide) return null;
                  if (c.breakpoint !== undefined && (screenWidth < c.breakpoint)) return null;
                  return <TableHeadCell key={i} className={c.className || ""}>{c.title}</TableHeadCell>;
                })}
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {(!loading && !error) && items.map((item, i) => {
                const selectedIndex = (selectedRows || []).indexOf(item._id);
                return (
                  <TableRow key={i} onClick={() => onClick(item)}>
                    {!!onChangeRowSelect && (
                      <TableCell
                        className="p-l-3 p-r-3"
                        style={{ width: "42px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const selected = [...(selectedRows || [])];
                          if (selectedIndex !== -1) {
                            selected.splice(selectedIndex, 1);
                          }
                          else {
                            selected.push(item._id);
                          }
                          onChangeRowSelect(selected);
                        }}>
                        {rowAlert && rowAlert(item) && <TableCellAlert><FaExclamation/></TableCellAlert>}
                        <Checkbox id={`select-${item._id}`} checked={selectedIndex !== -1}/>
                      </TableCell>
                    )}
                    {columns.map((c, k) => {
                      if (c.hide)
                        return null;
                      if (c.breakpoint !== undefined && (screenWidth < c.breakpoint))
                        return null;
                      return (
                        <TableCell
                          key={k}
                          style={{width: c.width ? `${c.width}px` : "auto" }}>
                          {(!onChangeRowSelect && k === 0 && rowAlert && rowAlert(item)) && <TableCellAlert><FaExclamation/></TableCellAlert>}
                          {c.render(item)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {loading && (
            <Loader>
              <RotateLoader size={4} className="center"/>
            </Loader>
          )}

          {(!loading && !error && items.length === 0) && (
            <Loader>
              <p className="text-center big">No Items Found</p>
            </Loader>
          )}

          {(!loading && error) && (
            <Loader>
              <p className="text-center error-text big">{error}</p>
            </Loader>
          )}
        </Content>

        <ProtableControls<T> store={this.store} onPageChange={onPageChange}/>

        <p className="small text-center m-t-2 font-semi-bold">Page</p>

      </Wrapper>
    );
  }

}
